/**
 * Cliente de la API de Aliados (tag `aliados` de `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.2):
 * - GET    /api/v1/admin/allies
 * - POST   /api/v1/admin/allies
 * - GET    /api/v1/admin/allies/{allyId}
 * - PATCH  /api/v1/admin/allies/{allyId}
 * - DELETE /api/v1/admin/allies/{allyId}
 * - POST   /api/v1/admin/allies/{allyId}/sync (reintento de sync del tenant, SUPER_ADMIN)
 * - POST   /api/v1/admin/allies/{allyId}/admins (F11: agregar admins después del alta)
 * - POST   /api/v1/admin/allies/{allyId}/admins/{userId}/reenroll (F11: regenerar invitación)
 *
 * Tipos derivados del contrato en `@goelectric/shared-types`; mismo patrón de red
 * que `nocApi` (sobre `apiRequest`).
 */
import type {
  AddAllyAdminsBody,
  AdminEmailsAvailability,
  Ally,
  AllyAdmin,
  AllyDocumentAvailability,
  AllyEmailAvailability,
  AlliesPage,
  CreateAllyBody,
  ListAlliesParams,
  UpdateAllyBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (filtros + offset/limit), tal como los define el contrato. */
export type AllyListParams = ListAlliesParams;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: AllyListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const allyApi = {
  list(params: AllyListParams = {}): Promise<AlliesPage> {
    return apiRequest<AlliesPage>(`/api/v1/admin/allies${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(allyId: string): Promise<Ally> {
    return apiRequest<Ally>(`/api/v1/admin/allies/${encodeURIComponent(allyId)}`);
  },

  create(body: CreateAllyBody): Promise<Ally> {
    return apiRequest<Ally>('/api/v1/admin/allies', { method: 'POST', body });
  },

  /**
   * TG-118: chequeo consultivo de unicidad al avanzar de paso. Devuelve solo el booleano,
   * sin datos del aliado existente. El endpoint de emails vive en el tag `usuarios`
   * (unicidad de `users`), pero lo consume el alta de aliados: se centraliza acá.
   */
  checkDocumentAvailability(documentNumber: string): Promise<AllyDocumentAvailability> {
    return apiRequest<AllyDocumentAvailability>(
      `/api/v1/admin/allies/document-availability?documentNumber=${encodeURIComponent(documentNumber)}`,
    );
  },

  /** TG-118/Q5: chequeo de unicidad del email del aliado (paso de contacto). */
  checkEmailAvailability(email: string): Promise<AllyEmailAvailability> {
    return apiRequest<AllyEmailAvailability>(
      `/api/v1/admin/allies/email-availability?email=${encodeURIComponent(email)}`,
    );
  },

  /** TG-118: chequeo bulk (`emails` coma-separados) para todas las filas del paso de admins. */
  checkEmailsAvailability(emails: string[]): Promise<AdminEmailsAvailability> {
    return apiRequest<AdminEmailsAvailability>(
      `/api/v1/admin/users/emails-availability?emails=${encodeURIComponent(emails.join(','))}`,
    );
  },

  update(allyId: string, body: UpdateAllyBody): Promise<Ally> {
    return apiRequest<Ally>(`/api/v1/admin/allies/${encodeURIComponent(allyId)}`, {
      method: 'PATCH',
      body,
    });
  },

  remove(allyId: string): Promise<void> {
    return apiRequest<void>(`/api/v1/admin/allies/${encodeURIComponent(allyId)}`, {
      method: 'DELETE',
    });
  },

  /** Reintenta la sincronización del tenant del aliado (ADR 0024); devuelve el aliado actualizado. */
  sync(allyId: string): Promise<Ally> {
    return apiRequest<Ally>(`/api/v1/admin/allies/${encodeURIComponent(allyId)}/sync`, {
      method: 'POST',
    });
  },

  /**
   * F11: agrega administradores locales a un aliado existente. Devuelve los admins creados
   * con su `inviteUrl` (único momento en que el token está disponible).
   */
  addAdmins(allyId: string, body: AddAllyAdminsBody): Promise<AllyAdmin[]> {
    return apiRequest<AllyAdmin[]>(`/api/v1/admin/allies/${encodeURIComponent(allyId)}/admins`, {
      method: 'POST',
      body,
    });
  },

  /**
   * F11: regenera la invitación de un admin en `PENDING` y devuelve su `inviteUrl` nuevo
   * (el anterior queda invalidado). El backend responde 409 si el admin no está `PENDING`.
   */
  reenrollAdmin(allyId: string, userId: string): Promise<AllyAdmin> {
    return apiRequest<AllyAdmin>(
      `/api/v1/admin/allies/${encodeURIComponent(allyId)}/admins/${encodeURIComponent(userId)}/reenroll`,
      { method: 'POST' },
    );
  },
};
